import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, BladApi, pobierzPlik } from '../api/client';
import { Karta, Komunikat, Ladowanie, Przycisk } from '../components/ui';
import { PrzyciskExcel } from '../components/PrzyciskExcel';

interface Katalog {
  pozycje: { kod: string; nazwa: string; opis: string }[];
  kopia: boolean;
}

/**
 * Wszystkie eksporty w jednym miejscu: arkusz z dowolnego działu bez filtrów
 * oraz — dla administratora — kopia całości. Filtrowane arkusze pobiera się
 * z samych list („Pobierz Excel” obok nagłówka).
 */
export function Eksporty() {
  const katalog = useQuery({ queryKey: ['eksport'], queryFn: () => api.get<Katalog>('/eksport') });
  const [kopiaTrwa, setKopiaTrwa] = useState(false);
  const [bladKopii, setBladKopii] = useState<string | null>(null);

  async function pobierzKopie() {
    setKopiaTrwa(true);
    setBladKopii(null);
    try {
      await pobierzPlik('/eksport/kopia', 'kopia-magazyn-csm.xlsx');
    } catch (e) {
      setBladKopii(e instanceof BladApi ? e.message : 'Nie udało się przygotować kopii.');
    } finally {
      setKopiaTrwa(false);
    }
  }

  if (katalog.isLoading) return <Ladowanie />;
  if (katalog.isError || !katalog.data) {
    return <Komunikat rodzaj="blad">Nie udało się wczytać listy eksportów. Odśwież stronę.</Komunikat>;
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-semibold text-kamien-900">Eksport do Excela</h1>
        <p className="mt-1 text-sm text-kamien-500">
          Każdy arkusz zawiera wszystkie wiersze działu. Chcesz tylko część — wejdź na listę,
          ustaw filtry i użyj tam przycisku „Pobierz Excel”. Każde pobranie zostaje w dzienniku operacji.
        </p>
      </div>

      {katalog.data.kopia && (
        <Karta
          tytul="Kopia całości"
          opis="Jeden plik z arkuszem na każdy dział: stany, kartoteka, dokumenty, zajęcia, serwis, użytkownicy, dziennik i reszta."
          akcje={(
            <Przycisk wariant="glowny" onClick={() => void pobierzKopie()} disabled={kopiaTrwa} aria-busy={kopiaTrwa}>
              {kopiaTrwa ? 'Przygotowuję kopię…' : 'Pobierz kopię całości'}
            </Przycisk>
          )}
        >
          <div className="flex flex-col gap-2 px-5 py-4 text-sm text-kamien-600">
            <p>
              To zestawienie danych do wglądu i archiwizacji. <strong className="font-medium text-kamien-800">Nie
              zastępuje kopii bazy danych</strong> — system odtwarza się z niej, nie z Excela. Plik zawiera dane
              osobowe pracowników (konta, dziennik), więc przechowuj go tak, jak inne dokumenty uczelni.
            </p>
            {bladKopii && <p role="alert" className="text-status-brak">{bladKopii}</p>}
          </div>
        </Karta>
      )}

      <Karta tytul="Arkusze według działów">
        <ul className="divide-y divide-kamien-200">
          {katalog.data.pozycje.map((d) => (
            <li key={d.kod} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
              <div className="min-w-64 flex-1">
                <p className="font-medium text-kamien-900">{d.nazwa}</p>
                <p className="text-sm text-kamien-500">{d.opis}</p>
              </div>
              <PrzyciskExcel dzial={d.kod} />
            </li>
          ))}
        </ul>
      </Karta>
    </div>
  );
}
